import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { DocumentUploader } from "./DocumentUploader";
import { AttachmentGallery } from "./AttachmentGallery";
import { useDropZone } from "../lib/useDropZone";
import { Paperclip, Plus, Trash2, X, Upload } from "lucide-react";

const MAX_ALLEGATI = 5;

export function AllegatiOffertaCard({ preventivo, user, onUpdate, readOnly = false, className = "", embedded = false }) {
  const [open, setOpen] = useState(false);
  const [nuovi, setNuovi] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);

  const list = preventivo.allegati_offerta || [];
  const rimasti = Math.max(0, MAX_ALLEGATI - list.length);
  const disabled = rimasti === 0;
  // Iter122s · distinzione titolare / tecnico condiviso negli allegati
  const tecniciIds = preventivo.tecnici_condivisi || [];
  const titolareId = preventivo.fornitore_id;

  // Iter122ab · Drag & drop direttamente sulla card: upload + commit automatico
  // NB: hook chiamato PRIMA di qualsiasi early return per rispettare le regole degli hooks
  const handleDrop = async (files) => {
    if (readOnly || disabled) {
      if (disabled) toast.error(`Limite raggiunto: max ${MAX_ALLEGATI} allegati`);
      return;
    }
    const arr = Array.from(files).slice(0, rimasti);
    if (arr.length === 0) return;
    setDropBusy(true);
    try {
      const ids = [];
      for (const file of arr) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/documento", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(data.id);
      }
      const { data } = await api.post(`/preventivi/${preventivo.id}/allegati-offerta`, { allegati_ids: ids });
      onUpdate?.(data);
      toast.success(ids.length === 1 ? "File aggiunto" : `${ids.length} file aggiunti`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito");
    } finally { setDropBusy(false); }
  };
  const { dragProps, isDragOver } = useDropZone(handleDrop, { disabled: readOnly || disabled || dropBusy });

  if (readOnly && list.length === 0) return null;

  // Iter122y · versione "embedded" senza il wrapper Card, per uso dentro altre card
  const Wrapper = embedded ? "div" : Card;
  const wrapperCls = embedded ? className : `p-4 border-2 border-slate-200 ${className}`;

  const salva = async () => {
    if (nuovi.length === 0) { toast.error("Seleziona almeno un file"); return; }
    if (nuovi.length > rimasti) { toast.error(`Puoi aggiungere ancora ${rimasti} file al massimo`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/preventivi/${preventivo.id}/allegati-offerta`, {
        allegati_ids: nuovi.map((n) => n.id),
      });
      onUpdate?.(data);
      toast.success(nuovi.length === 1 ? "File aggiunto" : `${nuovi.length} file aggiunti`);
      setNuovi([]);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Aggiunta non riuscita");
    } finally { setBusy(false); }
  };

  const rimuovi = async (fid, nome) => {
    if (!window.confirm(`Rimuovere "${nome}" dagli allegati offerta?`)) return;
    try {
      const { data } = await api.delete(`/preventivi/${preventivo.id}/allegati-offerta/${fid}`);
      onUpdate?.(data);
      toast.success("File rimosso");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rimozione non riuscita");
    }
  };

  const puoRimuovere = (a) => {
    if (readOnly) return false;
    if (user.role === "admin") return true;
    return a.caricato_ruolo === "Fornitore";
  };

  return (
    <Wrapper {...dragProps} className={`${wrapperCls} relative transition-all ${isDragOver ? "ring-2 ring-brand bg-brand/5" : ""}`} data-testid="allegati-offerta-card">
      {/* Iter122ab · overlay drag-over per feedback visivo */}
      {isDragOver && !readOnly && !disabled && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-brand/10 z-10">
          <div className="flex items-center gap-2 text-brand font-bold text-base bg-white/95 px-4 py-2 rounded-full border-2 border-brand shadow-md">
            <Upload className="w-5 h-5" /> Rilascia qui per allegare
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="font-display text-lg font-bold flex items-center gap-2">
          <Paperclip className="w-5 h-5" /> {embedded ? "Allegati preventivo" : "Allegati offerta"}
          <span className="text-sm font-semibold text-slate-500">({list.length}/{MAX_ALLEGATI})</span>
          {dropBusy && <span className="text-xs text-brand font-semibold animate-pulse">· Caricamento…</span>}
        </h4>
        {!readOnly && !open && (
          <Button data-testid="allegati-offerta-add-btn" size="sm"
            disabled={disabled || dropBusy} onClick={() => setOpen(true)}
            className="h-9 bg-brand hover:bg-brand-dark">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi file/foto
          </Button>
        )}
      </div>
      {!readOnly && (
        <p className="text-sm text-slate-500 mb-3">
          Puoi caricare file/foto (max {MAX_ALLEGATI}) relativi all'offerta — <b>trascina qui i file</b> oppure usa il bottone.
          Ogni aggiunta o rimozione viene registrata nella cronologia.
        </p>
      )}

      {list.length > 0 ? (
        <div className="space-y-3">
          <AttachmentGallery docs={list} testidPrefix="allegato-offerta" />
          <div className="space-y-1">
            {list.map((a) => {
              const isTecnico = a.caricato_ruolo === "Fornitore" && a.caricato_fornitore_id && tecniciIds.includes(a.caricato_fornitore_id);
              const isTitolare = a.caricato_ruolo === "Fornitore" && !isTecnico && (!a.caricato_fornitore_id || a.caricato_fornitore_id === titolareId);
              return (
              <div key={a.id} data-testid={`allegato-offerta-meta-${a.id}`}
                className={`flex items-center justify-between gap-2 text-xs text-slate-500 border-t pt-1 ${isTecnico ? "border-indigo-200 bg-indigo-50/40 -mx-2 px-2 rounded" : "border-slate-100"}`}>
                <span className="flex items-center gap-1.5 flex-wrap">
                  <b className="text-slate-700">{a.original_filename}</b>
                  {isTecnico && (
                    <span data-testid={`allegato-badge-tecnico-${a.id}`} className="inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-600 text-white">Tecnico condiviso</span>
                  )}
                  {isTitolare && tecniciIds.length > 0 && (
                    <span data-testid={`allegato-badge-titolare-${a.id}`} className="inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-600 text-white">Fornitore titolare</span>
                  )}
                  <span>— caricato da {a.caricato_da} ({a.caricato_ruolo}) il {new Date(a.caricato_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                </span>
                {puoRimuovere(a) && (
                  <button type="button" data-testid={`allegato-offerta-del-${a.id}`}
                    onClick={() => rimuovi(a.id, a.original_filename)}
                    className="text-red-600 hover:text-red-800 shrink-0" title="Rimuovi allegato">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic">Nessun allegato caricato.</p>
      )}

      {open && (
        <div className="mt-4 pt-4 border-t border-slate-200 space-y-3" data-testid="allegati-offerta-form">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-bold text-slate-700">Nuovi allegati (rimanenti: {rimasti})</span>
            <button type="button" onClick={() => { setOpen(false); setNuovi([]); }}
              className="text-slate-400 hover:text-slate-700" title="Annulla">
              <X className="w-4 h-4" />
            </button>
          </div>
          <DocumentUploader docs={nuovi} onChange={setNuovi} testid="allegati-offerta-uploader" />
          <div className="actions-row">
            <Button data-testid="allegati-offerta-save-btn" disabled={busy || nuovi.length === 0}
              onClick={salva} className="h-10 bg-brand hover:bg-brand-dark">
              {busy ? "Caricamento..." : `Salva (${nuovi.length})`}
            </Button>
            <Button variant="outline" onClick={() => { setOpen(false); setNuovi([]); }} className="h-10">
              Annulla
            </Button>
          </div>
        </div>
      )}
    </Wrapper>
  );
}
